import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Users, Wallet, TrendingUp, Activity, AlertCircle, Clock, AlertTriangle, FileQuestion } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [pendingItems, setPendingItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    loadPendingItems();
  }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await adminAPI.getDashboard();
      setDashboard(data);
    } catch (error) {
      toast.error('Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingItems = async () => {
    try {
      const { data } = await adminAPI.getPendingItems();
      setPendingItems(data);
    } catch (error) {
      console.error('Failed to load pending items:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        <div>
          <h1 className="text-4xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="mt-2 text-muted-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Platform overview and management
          </p>
        </div>

        {/* Needs Attention Panel */}
        {pendingItems && (pendingItems.pending_withdrawals > 0 || 
          pendingItems.pending_deposits > 0 || 
          pendingItems.open_support_tickets > 0 || 
          pendingItems.open_disputes > 0 ||
          pendingItems.pending_runner_applications > 0) && (
          <Card className="border-l-4 border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                <AlertCircle className="h-5 w-5" />
                Needs Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {pendingItems.pending_withdrawals > 0 && (
                  <Link to="/admin/withdrawals" className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors border border-border">
                    <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Pending Withdrawals</p>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">{pendingItems.pending_withdrawals}</p>
                    </div>
                  </Link>
                )}
                
                {pendingItems.pending_deposits > 0 && (
                  <Link to="/admin/deposits" className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors border border-border">
                    <div className="h-10 w-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Pending Deposits</p>
                      <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{pendingItems.pending_deposits}</p>
                    </div>
                  </Link>
                )}

                {pendingItems.open_support_tickets > 0 && (
                  <Link to="/admin/support" className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors border border-border">
                    <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <FileQuestion className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Open Support Tickets</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{pendingItems.open_support_tickets}</p>
                    </div>
                  </Link>
                )}

                {pendingItems.open_disputes > 0 && (
                  <Link to="/admin/disputes" className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors border border-border">
                    <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Open Disputes</p>
                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{pendingItems.open_disputes}</p>
                    </div>
                  </Link>
                )}

                {pendingItems.pending_runner_applications > 0 && (
                  <Link to="/admin/pickup" className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors border border-border">
                    <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Pending Runner Apps</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{pendingItems.pending_runner_applications}</p>
                    </div>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border border-border hover:shadow-lg transition-shadow" data-testid="total-users-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {dashboard?.total_users || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Registered users</p>
            </CardContent>
          </Card>

          <Card className="border border-border hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Currencies</CardTitle>
              <Wallet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {dashboard?.total_currencies || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Active currencies</p>
            </CardContent>
          </Card>

          <Card className="border border-border hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Deposits</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold text-yellow-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {dashboard?.pending_deposits || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card className="border border-border hover:shadow-lg transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending Withdrawals</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold text-orange-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {dashboard?.pending_withdrawals || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting processing</p>
            </CardContent>
          </Card>
        </div>

        {dashboard?.currency_balances && Object.keys(dashboard.currency_balances).length > 0 && (
          <Card className="border border-border">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Currency Balances</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(dashboard.currency_balances).map(([currency, balance]) => (
                  <div
                    key={currency}
                    className="p-4 rounded-lg bg-muted/50 border border-border"
                    data-testid={`currency-balance-${currency}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                          {currency.substring(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{currency}</p>
                          <p className="text-xs text-muted-foreground">Total in circulation</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {formatCurrency(balance, currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {dashboard?.recent_transactions && dashboard.recent_transactions.length > 0 && (
          <Card className="border border-border">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboard.recent_transactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`admin-transaction-${tx.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex flex-col">
                        <p className="font-medium text-foreground capitalize">{tx.type.replace('_', ' ')}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatCurrency(tx.amount, tx.currency)}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize">{tx.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};
